import React, { useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiMoreVertical, FiEdit2, FiTrash2, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { apiUrl } from '../../config/api';
import { USER_LIST, USER_DELETE } from '../../api/contracts';
import './Page.css';
import './Category.css';

const COL_COUNT = 9;
const MIN_WIDTHS_PX = [80, 100, 70, 120, 100, 90, 70, 80, 70];
const INITIAL_WIDTHS_PCT = [14, 12, 8, 14, 14, 10, 8, 10, 10];
const PAGE_SIZE = 50;

function formatValue(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

function User() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [actionMenuId, setActionMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [columnWidths, setColumnWidths] = useState(INITIAL_WIDTHS_PCT);
  const [resizeState, setResizeState] = useState({ index: null, startX: 0, startWidths: [] });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const tableRef = useRef(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.trim().toLowerCase();
    return users.filter(
      (u) =>
        (u.user_id && u.user_id.toLowerCase().includes(q)) ||
        (u.name && u.name.toLowerCase().includes(q)) ||
        (u.email && u.email.toLowerCase().includes(q)) ||
        (u.phone_number && u.phone_number.includes(q))
    );
  }, [users, search]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginated = filtered.slice(startIndex, startIndex + PAGE_SIZE);
  const displayStart = totalItems === 0 ? 0 : startIndex + 1;
  const displayEnd = totalItems === 0 ? 0 : Math.min(startIndex + PAGE_SIZE, totalItems);

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, totalPages));
  }, [totalPages, totalItems]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useLayoutEffect(() => {
    if (!actionMenuId || !triggerRef.current) {
      setMenuPosition(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, [actionMenuId]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (actionMenuId && !triggerRef.current?.contains(e.target) && !menuRef.current?.contains(e.target)) {
        setActionMenuId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionMenuId]);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(apiUrl(USER_LIST.path), { method: USER_LIST.method, headers })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        if (cancelled) return;
        const list = data.data || data;
        setUsers(Array.isArray(list) ? list : []);
        setListError(Array.isArray(list) ? '' : 'Invalid response from server.');
      })
      .catch((err) => {
        if (!cancelled) setListError(err.message || 'Failed to load users.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleEdit = (row) => {
    setActionMenuId(null);
    navigate(`/dashboard/user/${encodeURIComponent(row.user_id)}/edit`);
  };

  const handleRemove = async (row) => {
    setActionMenuId(null);
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await fetch(apiUrl(USER_DELETE.pathFor(row.user_id)), {
        method: USER_DELETE.method,
        headers,
      });
      if (res.ok) {
        setUsers((prev) => prev.filter((u) => u.user_id !== row.user_id));
      }
    } catch {
      // keep list as is
    }
  };

  const handleResizeStart = (handleIndex, e) => {
    e.preventDefault();
    setResizeState({ index: handleIndex, startX: e.clientX, startWidths: [...columnWidths] });
  };

  useEffect(() => {
    if (resizeState.index === null) return;
    const table = tableRef.current;
    if (!table) return;
    const onMove = (e) => {
      const tw = table.offsetWidth;
      if (tw <= 0) return;
      const i = resizeState.index;
      const minLeftPct = (MIN_WIDTHS_PX[i] / tw) * 100;
      const minRightPct = (MIN_WIDTHS_PX[i + 1] / tw) * 100;
      const totalTwo = resizeState.startWidths[i] + resizeState.startWidths[i + 1];
      const deltaPct = ((e.clientX - resizeState.startX) / tw) * 100;
      let newLeft = resizeState.startWidths[i] + deltaPct;
      newLeft = Math.max(minLeftPct, Math.min(totalTwo - minRightPct, newLeft));
      const newRight = totalTwo - newLeft;
      setColumnWidths((prev) => {
        const next = [...prev];
        next[i] = newLeft;
        next[i + 1] = newRight;
        return next;
      });
    };
    const onUp = () => setResizeState((s) => ({ ...s, index: null }));
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [resizeState.index, resizeState.startX, resizeState.startWidths]);

  const headers = ['User ID', 'Name', 'Age group', 'Phone number', 'Email', 'Google ID', 'Is paid', 'Status', 'Action'];

  return (
    <div className="page category-page">
      <div className="category-header">
        <h3>User</h3>
        <p className="category-subtitle">Manage users</p>
      </div>

      <div className="category-toolbar">
        <div className="search-wrap">
          <FiSearch className="search-icon" aria-hidden />
          <input
            type="text"
            className="search-input"
            placeholder="Search by user ID, name, email or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search users"
          />
        </div>
      </div>

      {loading && <p className="create-category-loading">Loading users…</p>}
      {listError && <p className="create-category-error" role="alert">{listError}</p>}

      <div className="table-wrap">
        <table ref={tableRef} className="category-table category-table-resizable">
          <colgroup>
            {columnWidths.map((w, i) => (
              <col key={i} style={{ width: `${w}%`, minWidth: MIN_WIDTHS_PX[i] }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {headers.map((label, i) => (
                <th key={i} className={i === COL_COUNT - 1 ? 'th-action' : ''} style={{ width: `${columnWidths[i]}%` }}>
                  <span className="th-inner">{label}</span>
                  {i < COL_COUNT - 1 && (
                    <span
                      className="resize-handle"
                      onMouseDown={(e) => handleResizeStart(i, e)}
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Resize column ${i + 1}`}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={COL_COUNT} className="table-empty">
                  No users found.
                </td>
              </tr>
            ) : (
              paginated.map((row) => (
                <tr key={row.user_id}>
                  <td className="cell-id">
                    <span className="cell-ellipsis" title={row.user_id || ''}>
                      {formatValue(row.user_id)}
                    </span>
                  </td>
                  <td className="cell-name">
                    <span className="cell-ellipsis" title={row.name || ''}>
                      {formatValue(row.name)}
                    </span>
                  </td>
                  <td>
                    <span className="cell-ellipsis" title={row.age_group === '<3' ? '0-3' : (row.age_group || '')}>
                      {formatValue(row.age_group === '<3' ? '0-3' : row.age_group)}
                    </span>
                  </td>
                  <td>
                    <span className="cell-ellipsis" title={row.phone_number || ''}>
                      {formatValue(row.phone_number)}
                    </span>
                  </td>
                  <td>
                    <span className="cell-ellipsis" title={row.email || ''}>
                      {formatValue(row.email)}
                    </span>
                  </td>
                  <td>
                    <span className="cell-ellipsis" title={row.google_id || ''}>
                      {formatValue(row.google_id)}
                    </span>
                  </td>
                  <td>
                    <span className={`status-pill status-${row.is_paid ? 'active' : 'inactive'}`}>
                      {row.is_paid ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-pill status-${(row.status || '').toLowerCase()}`}>
                      {formatValue(row.status)}
                    </span>
                  </td>
                  <td className="cell-action">
                    <div className="action-wrap">
                      <button
                        ref={actionMenuId === row.user_id ? triggerRef : null}
                        type="button"
                        className="btn-dots"
                        onClick={() => setActionMenuId(actionMenuId === row.user_id ? null : row.user_id)}
                        aria-label="Open menu"
                        aria-expanded={actionMenuId === row.user_id}
                      >
                        <FiMoreVertical className="dots-icon" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalItems > 0 && (
        <div className="pagination-wrap">
          <button
            type="button"
            className="pagination-btn"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <FiChevronLeft className="pagination-arrow" aria-hidden />
          </button>
          <span className="pagination-text">
            Showing {displayStart}–{displayEnd} of {totalItems}
          </span>
          <button
            type="button"
            className="pagination-btn"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            aria-label="Next page"
          >
            <FiChevronRight className="pagination-arrow" aria-hidden />
          </button>
        </div>
      )}

      {actionMenuId && menuPosition &&
        (() => {
          const row = filtered.find((u) => u.user_id === actionMenuId);
          if (!row) return null;
          const isInactive = (row.status || '').toLowerCase() === 'inactive';
          return createPortal(
            <div
              ref={menuRef}
              className="action-menu action-menu-portal"
              style={{ position: 'fixed', top: menuPosition.top, left: menuPosition.left, zIndex: 1000 }}
            >
              <button type="button" className="action-menu-item" onClick={() => handleEdit(row)}>
                <FiEdit2 className="action-menu-icon" />
                Edit user
              </button>
              <button
                type="button"
                className="action-menu-item"
                disabled={!isInactive}
                onClick={() => isInactive && handleRemove(row)}
                title={!isInactive ? 'Only inactive users can be removed' : 'Remove user'}
              >
                <FiTrash2 className="action-menu-icon" />
                Remove user
              </button>
            </div>,
            document.body
          );
        })()}
    </div>
  );
}

export default User;
